/**
 * Add Contains Modal
 *
 * Creates one or more passive 'contains' edges from a parent to children.
 * Canon ref: 07-item-relationship-taxonomy-and-mutation-rules.md §5.8
 */

import { useState, useEffect } from 'react';
import { X, CornerDownRight, AlertCircle, XCircle, Plus } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import {
  containsEdgeValidationMessage,
  validateContainsEdge,
} from '../../../lib/containmentHierarchy';

interface AddContainsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** Pre-populate the parent when opened from item detail. */
  sourceItemId?: string;
}

export function AddContainsModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: AddContainsModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const existingEdges = useItemRelationshipsStore((s) => s.edges);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const allItems = useItemsStore((s) => s.items);

  const [parentItemId, setParentItemId] = useState<string>(sourceItemId ?? '');
  const [childItemIds, setChildItemIds] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setParentItemId(sourceItemId ?? '');
    setChildItemIds([]);
    setPickerKey((k) => k + 1);
    setRationale('');
    setError(null);
  }, [isOpen, sourceItemId]);

  const handleAddChild = (id: string | undefined) => {
    if (!id) return;
    if (id === parentItemId) { setError('Child cannot be the same as the parent'); return; }
    if (childItemIds.includes(id)) return;
    if (!parentItemId) { setError('Select the parent item first'); return; }
    const violation = validateContainsEdge(parentItemId, id, existingEdges);
    if (violation) {
      setError(containsEdgeValidationMessage(violation));
      return;
    }
    setError(null);
    setChildItemIds((prev) => [...prev, id]);
    setPickerKey((k) => k + 1);
  };

  const handleRemoveChild = (id: string) => {
    setChildItemIds((prev) => prev.filter((c) => c !== id));
  };

  const getTitle = (id: string) =>
    allItems.find((i) => i.id === id)?.title ?? id;

  const handleCreate = async () => {
    setError(null);
    if (!parentItemId) { setError('Select the parent item'); return; }
    if (childItemIds.length === 0) { setError('Add at least one child item'); return; }

    setIsCreating(true);
    let lastEdgeId: string | null = null;
    try {
      for (const childId of childItemIds) {
        const violation = validateContainsEdge(parentItemId, childId, existingEdges);
        if (violation) {
          setError(containsEdgeValidationMessage(violation));
          return;
        }
        const edge = await createEdge({
          type: 'contains',
          directionality: 'directed',
          status: 'active',
          declaredMode: 'declared',
          sourceItemId: parentItemId,
          targetItemId: childId,
          rationale: rationale.trim() || undefined,
        });
        if (!edge) {
          setError(
            useItemRelationshipsStore.getState().syncError ??
              `Failed to create Contains relationship for "${getTitle(childId)}"`,
          );
          return;
        }
        lastEdgeId = edge.id;
      }
      if (lastEdgeId) {
        onCreated?.(lastEdgeId);
        onClose();
      } else {
        setError('Failed to create Contains relationship');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Contains relationship');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const canSubmit = !!parentItemId && childItemIds.length > 0 && !isCreating;
  const excludeIds = [parentItemId, ...childItemIds].filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <CornerDownRight className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Contains</h2>
              <p className="text-sm text-[var(--text-ter)]">Passive hierarchy — parent owns children for reporting</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Parent item (container)</label>
            <SearchableItemPicker
              key={`parent-${pickerKey}`}
              items={allItems}
              excludeIds={excludeIds}
              value={parentItemId}
              onChange={(id) => { setParentItemId(id ?? ''); setError(null); }}
              placeholder="Select the epic, theme, or parent item…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Child items</label>
            {childItemIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {childItemIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]">
                    {getTitle(id)}
                    <button type="button" onClick={() => handleRemoveChild(id)} className="hover:text-[var(--text-ter)]">
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <SearchableItemPicker
              key={`child-${pickerKey}`}
              items={allItems}
              excludeIds={excludeIds}
              onChange={handleAddChild}
              placeholder="Add a child item…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Note (optional)</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
              placeholder="Why does this parent own these children?"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Create Contains
          </button>
        </div>
      </div>
    </div>
  );
}
